<template>
  <div 
    v-click-outside="close"
    class="bg-picker" 
  >
    <div 
      class="sp-logo-bar w-100 bg-picker__trigger"
      @click="toggle"
    >
      <button 
        class="sp-logo-btn"
      >
        <svg 
          width="15" 
          height="15" 
          viewBox="0 0 15 15" 
          fill="none"
        >
          <rect 
            x="1" 
            y="2" 
            width="13" 
            height="11" 
            rx="1.5" 
            stroke="currentColor" 
            stroke-width="1.3" 
            fill="none"
          />
          <path 
            d="M1 9l3.5-3.5 3 3L10.5 5l3.5 4" 
            stroke="currentColor" 
            stroke-width="1.3" 
            stroke-linecap="round" 
            stroke-linejoin="round" 
            fill="none"
          />
          <circle 
            cx="5" 
            cy="6" 
            r="1" 
            fill="currentColor"
          />
        </svg>
        Add logo
      </button>
      <button 
        title="Add"
        class="sp-icon-btn"
      >
        <svg 
          width="13" 
          height="13" 
          viewBox="0 0 13 13" 
          fill="none"
        >
          <path 
            d="M6.5 1v11M1 6.5h11" 
            stroke="currentColor" 
            stroke-width="1.5" 
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>

    <transition name="bg-picker__panel">
      <div
        v-if="isOpen"
        class="bg-picker__panel"
        role="menu"
        :style="panelPositionStyle"
      >
        <div class="d-flex align-items-center justify-content-between">
          <p class="bg-picker__panel-title">
            Logo Library
          </p>
          <button 
            class="bg-picker__option" 
            role="menuitem" 
            @click="triggerUpload"
          >
            <span class="bg-picker__option-icon">
              <i class="bi bi-upload" />
            </span>
            Upload
          </button>
        </div>

        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="bg-picker__file-input"
          @change="onFileSelected"
        >
        <div
          v-if="availableLogos.length"
          class="logo-list"
        >
          <div
            v-for="logo in availableLogos"
            :key="logo.id"
            class="logo-item"
          >
            <div class="logo-item__thumb">
              <img
                :src="logo.previewUrl ?? logo.thumbnail_path"
                :alt="logo.fileName || 'Logo'"
                @click="addLogo(null, null, logo.original_path ?? null)"
              >
            </div>
          </div>
        </div>
        <div 
          v-if="!availableLogos.length"
          class="empty-folder"
        >
          <p>
            No logos yet. Click <strong>Upload</strong> to add one.
          </p>
        </div>
        <div class="d-flex align-items-center justify-content-end border-t pt-1 mt-1">
          <button 
            class="file-browser-btn bg-picker__option"
            title="Rename, delete, or organize logos in the File Browser"
            @click="redirectToFileBrowser"
          >
            <i class="bi bi-folder" />
            Manage in File Browser
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker';
import API_MEDIA from "@frontend/services/api/media";
import { addAddField } from "@frontend/group/modules/additional-fields";
import { FIELD_TYPE_BACKGROND_THEME_IMAGE, FIELD_TYPE_PRODUCT_IMAGE } from '@frontend/constants/type-fields-of-template';
import { array } from 'three/src/nodes/core/ArrayNode.js';
import { IMAGE_PREFIX_ID } from '@frontend/constants/templates-field-prefixes';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { uploadFile as uploadSingleFile, uploadFolderFile } from '@frontend/services/file-uploader';
import Requests from "../../services/api/file-browser";
import {
  DISPLAY_BUILDER_LAYER_META_KEY,
  DISPLAY_BUILDER_LAYER_ROLE_LOGO,
  createDisplayBuilderAssetKey,
  getAdditionalBackgroundIndex,
  normalizeDisplayBuilderLayerMeta,
} from '@frontend/components/display-builder/display-layer-metadata';
import { getMinimumOverlapCorrection } from '@frontend/components/display-builder/layout-defaults';

const LOGOS_DEFAULT_FOLDER_NAME = 'Display Builder Logos';
const getShowPageScope = () => ShowPageScopeCollector.get([
  'handleObjectMoving',
  'getChangesLayoutById',
  'updateLayoutChanges',
]);

export default {
  name: 'LogoUploader',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!el.contains(event.target)) binding.value(event);
        };
        document.addEventListener('mousedown', el._clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener('mousedown', el._clickOutsideHandler);
      },
    },
  },
  data() {
    return {
      isOpen: false,
      folderId: null,
      logos: [],
    };
  },

  computed: {
    panelPositionStyle() {
      const map = {
        'bottom-right': { top: 'calc(100% + 6px)', right: '0' },
        'bottom-left':  { top: 'calc(100% + 6px)', left: '0' },
        'top-right':    { bottom: 'calc(100% + 6px)', right: '0' },
        'top-left':     { bottom: 'calc(100% + 6px)', left: '0' },
      };
      return map[this.placement] || map['bottom-right'];
    },
    availableLogos() {
      return this.logos.filter(
        logo => !!logo.thumbnail_path || !!logo.previewUrl
      );
    }
  },
  mounted() {
    window.displayBuilderLogoUploader = this;
    this.handleLogosFolder();
  },

  beforeDestroy() {
    if (window.displayBuilderLogoUploader === this) {
      window.displayBuilderLogoUploader = null;
    }
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },

    close() {
      this.isOpen = false;
    },

    triggerUpload() {
      this.$refs.fileInput.click();
    },

    async onFileSelected(event) {
      const file = event.target.files[0];

      if (!file) {
        return;
      }

      await this.uploadFileToBrowser(file)
    },
    async addLogo(file = null, position = null, path = null) {
      const { id: templateId, customer_id, preview_id } = window.template_data;
      const canvas = window.previews[0]?.canvas;

      if (templateId == null || customer_id == null || preview_id == null) {
        toastr.error('Template data is incomplete');
        return;
      }

      if (!file && !path) {
        toastr.error('No file selected');
        return;
      }
      $('.loadingOverlay').fadeIn();
      $('.loadingOverlay-text').html('Adding Logo Image...');

      try {
        let backgroundUrl = null;
        if (file) {
          const formData = new FormData();

          formData.append('filename_1', file.name);
          formData.append('customer_id', customer_id);
          formData.append('template_id', templateId);
          formData.append('file_1', file);
          formData.append('remove_background', 0);

          const uploadResponse = await API_MEDIA.uploadBackgroundImage(formData);

          if (!uploadResponse?.data?.[0]?.path) {
            console.error('Invalid upload response:', uploadResponse);
            toastr.error('Upload succeeded but returned invalid response');
            $('.loadingOverlay').fadeOut();
            return;
          }

          backgroundUrl = `/share?file=${uploadResponse.data[0].path}`;
        } 
        
        if (path) {
          // e.g path = user-folder/27946/user-files/1779375945.png
          backgroundUrl = `/share?file=${path}`;
        }

        const beforeObjectIds = this.getCanvasObjectIds();

        await addAddField(
          FIELD_TYPE_BACKGROND_THEME_IMAGE,
          preview_id,
          {
            additional_background_images: backgroundUrl,
          }
        );

        setTimeout(() => {
          const newObject = canvas.getObjects().find((obj) => {
            return obj.id && !beforeObjectIds.includes(String(obj?.id));
          });

          if (!newObject) {
            console.warn('[Logo object not found');
            $('.loadingOverlay').fadeOut();
            return;
          }

          newObject.displayBuilderType = 'display-logo';

          const { getChangesLayoutById, updateLayoutChanges } = getShowPageScope();
          const changes = getChangesLayoutById?.(preview_id) || {};
          const metadata = normalizeDisplayBuilderLayerMeta(
            changes[DISPLAY_BUILDER_LAYER_META_KEY]
              ?? window.previews[0]?.data?.[DISPLAY_BUILDER_LAYER_META_KEY]
          );
          const metadataIndex = getAdditionalBackgroundIndex(newObject);
          const assetKey = createDisplayBuilderAssetKey();

          if (metadataIndex !== null) {
            metadata[metadataIndex] = {
              ...(metadata[metadataIndex] || {}),
              assetKey,
              role: DISPLAY_BUILDER_LAYER_ROLE_LOGO,
            };
            newObject.displayBuilderAssetKey = assetKey;
            updateLayoutChanges?.(preview_id, DISPLAY_BUILDER_LAYER_META_KEY, metadata);
            window.displayBuilderHelper?.setDisplayLayerMetadata?.(metadata);
          }

          newObject.set({
            displayBuilderType: 'display-logo',
            // Clear locks inherited while the image was treated as a background.
            locked: false,
            selectable: true,
            evented: true,
            hasControls: true,
            hasBorders: true,
            hoverCursor: 'move',
            objectCaching: false,
          });

          if (position) {
            newObject.set({
              left: position.positionX,
              top: position.positionY
            })
          }

          newObject.moveTo(10000);
          canvas.setActiveObject(newObject);
          newObject.setCoords();

          const { dx, dy } = getMinimumOverlapCorrection(newObject.getCoords(true, true));

          if (dx || dy) {
            newObject.set({
              left: newObject.left + dx,
              top: newObject.top + dy,
            });
            newObject.setCoords();
          }

          const { handleObjectMoving } = getShowPageScope();

          if (handleObjectMoving) {
            handleObjectMoving({ target: newObject });
          }

          window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
          canvas.requestRenderAll();

          let thumbnail = null;
          if (newObject._element && newObject?.id.startsWith(IMAGE_PREFIX_ID)) {
            const url = new URL(newObject._element.currentSrc);
            if (url.searchParams.size > 0) {
              url.searchParams.set('lightweight', '');
              url.searchParams.set('scale_width', '40');
            }
            thumbnail = url;
          } else if (newObject._element && !newObject?.id.startsWith(IMAGE_PREFIX_ID)) {
            const url = new URL(newObject._element.currentSrc);
            if (url.searchParams.size > 0) {
              url.searchParams.set('lightweight', '');
              url.searchParams.set('is_thumbnail', '1')
            }
            thumbnail = url;
          }

          window.dispatchEvent(new CustomEvent('display-builder:logo-added', {
            detail: {
              canvasObjectId: newObject?.id,
              assetKey,
              object: newObject,
              fileName: file?.name,
              thumbnail: thumbnail,
            },
          }));
        }, 300);

        this.close();

        if (file && !path) {
          await this.uploadFileToBrowser(file);
        }

        $('.loadingOverlay').fadeOut();
      } catch (err) {
        $('.loadingOverlay').fadeOut();
        console.error('addBackgroundImage error:', err);
        toastr.error(err.message || 'Unexpected error while uploading background image');
      }
    },
    getCanvasObjectIds() {
      return window.previews[0]?.canvas.getObjects()
        .map((obj) => obj.id)
        .filter(Boolean)
        .map(String);
    },
    async addLogoFromFile(file, position = null) {
      return this.addLogo(file, position);
    },
    async handleLogosFolder() {
      const response = await axios.post(`/file/check_folder_name_exists`,{
        name: LOGOS_DEFAULT_FOLDER_NAME,
        parent_id: null,
        isTemplateBox: true,
      });

      if (response.data.exists) {
        this.folderId = response.data.folderId;
        await this.handleFolderContent(this.folderId);
        return;
      } 

      const responseFolder = await axios.post('/file/create_folder_by_name', {
        name: LOGOS_DEFAULT_FOLDER_NAME,
        parent_id: null,
      });
      this.folderId = responseFolder.data.id;
    },
    async handleFolderContent(folderId) {
      const { data } = await axios.get(`/file/folder_content?folderId=${folderId}&pageNumber=1&pageSize=25`);

      this.logos = data?.items;
    },
    async uploadFileToBrowser(file) {
      let fileFolder = await uploadFolderFile(
        [file],
        this.folderId
      );
      
      if (fileFolder[0].isExist) {
        this.fileToBrowserLoading = false;
        // file exists
        toastr.error(
          `File already exists!`
        );

        return;
      }

      await this.uploadFileChunk({
        ...fileFolder[0],
        file: file
      });

      await this.handleLogosFolder();
    },
    async uploadFileChunk(payload) {
      const CHUNK_SIZE = 5 * 1024 * 1024;

      const size = payload.data.size;
      const {file, ...data} = payload;
      const chunks = Math.ceil(size / CHUNK_SIZE);
      let chunk = 1;

      while (chunk <= chunks) {
          const offset = (chunk - 1) * CHUNK_SIZE;
          const signed_res = await Requests.signPartUpload({ ...data, partNumber: chunk });
          const chunk_file = file.slice(offset, offset + CHUNK_SIZE);
          await uploadSingleFile(signed_res.data.url, chunk_file);
          chunk++;
      }

      const res = await Requests.getMultipartUploadedParts(data);
      await Requests.completeMultipartUpload({
          ...data,
          parts: res.data.parts,
      });
      this.fileToBrowserLoading = false;
      toastr.success(`Upload successfully completed`);
    },
    async redirectToFileBrowser() {
      const $saveBtn = $(".btn-save-changes");
      if ($saveBtn.length && !$saveBtn.prop('disabled')) {
          // if changes are not saved, wait for save to complete by listening to loading overlay then save default state
          await new Promise((resolve) => {
              $saveBtn.click();
              setTimeout(() => {
                  const checkSaveComplete = setInterval(() => {
                      if (!$('.loadingOverlay').is(':visible')) {
                          clearInterval(checkSaveComplete);
                          resolve();
                      }
                  }, 500);
                  setTimeout(() => {
                      clearInterval(checkSaveComplete);
                      resolve();
                  }, 30000);
              }, 1000);
          });
      }

      window.location.href = `/file/browser/files/${this.folderId}?view=grid&page=1&search=&pageStack=91`;
    }
  },
};
</script>

<style scoped>
.bg-picker {
  --bg-picker-teal:      #2eb89a;
  --bg-picker-teal-dim:  rgba(46, 184, 154, 0.12);
  --bg-picker-border:    #e4e7eb;
  --bg-picker-bg:        #ffffff;
  --bg-picker-surface:   #f8f9fb;
  --bg-picker-hover:     #f2f4f7;
  --bg-picker-text:      #1a1d23;
  --bg-picker-muted:     #6b7280;
  --bg-picker-light:     #9ca3af;
  --bg-picker-danger:    #ef4444;
  --bg-picker-danger-bg: #fef2f2;
  --bg-picker-radius:    8px;
  --bg-picker-radius-sm: 5px;
  --bg-picker-shadow:    0 6px 20px rgba(0,0,0,.11), 0 1px 4px rgba(0,0,0,.07);
  --bg-picker-font:      'DM Sans', system-ui, sans-serif;
}

.bg-picker {
  position: relative;
  display: inline-flex;
}

.bg-picker__trigger {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-picker-bg);
  border: none;
  border-radius: var(--bg-picker-radius-sm);
  color: var(--bg-picker-muted);
  cursor: pointer;
  padding: 0;
  transition: background 0.14s, border-color 0.14s, color 0.14s, box-shadow 0.14s;
  flex-shrink: 0;
  overflow: hidden;
}

.bg-picker__trigger:hover {
  background: var(--bg-picker-surface);
  border-color: #c9cdd4;
  color: var(--bg-picker-text);
}

.bg-picker__trigger.is-open {
  border-color: var(--bg-picker-teal);
  color: var(--bg-picker-teal);
  background: var(--bg-picker-teal-dim);
  box-shadow: 0 0 0 3px rgba(46, 184, 154, 0.15);
}

.bg-picker__swatch {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: calc(var(--bg-picker-radius-sm) - 1px);
}

.bg-picker__panel {
  position: absolute;
  z-index: 1050;
  width: 16rem;
  background: var(--bg-picker-bg);
  border: 1px solid var(--bg-picker-border);
  border-radius: var(--bg-picker-radius);
  box-shadow: var(--bg-picker-shadow);
  padding: 10px;
  outline: none;
}

.bg-picker__panel-title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--bg-picker-light);
  margin: 0;
  padding: 4px 12px 8px;
}

.bg-picker__option {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 11px;
  color: var(--teal);
  background: transparent;
  border: none;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  line-height: 1.3;
}

.bg-picker__option:hover {
  text-decoration: underline;
}

.bg-picker__option--danger {
  color: var(--bg-picker-danger);
}

.bg-picker__option--danger:hover {
  background: var(--bg-picker-danger-bg);
}

.bg-picker__option-icon {
  display: flex;
  align-items: center;
  color: var(--teal);
  flex-shrink: 0;
}

.bg-picker__option--danger .bg-picker__option-icon {
  color: var(--bg-picker-danger);
}

.bg-picker__file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.bg-picker__panel-enter-active,
.bg-picker__panel-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.bg-picker__panel-enter,
.bg-picker__panel-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.97);
}
/* ── Logo bar ── */
.sp-logo-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.75rem;
  border: 1px solid var(--border);
  border-radius: 6px;
}

.sp-logo-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 500;
  color: var(--teal);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity .15s;
}
.sp-logo-btn:hover { opacity: .75; }
/* ── Icon button ── */
.sp-icon-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-light);
  cursor: pointer;
  transition: background .13s, color .13s, border-color .13s;
}
.sp-icon-btn:hover,
.sp-icon-btn.active {
  background: var(--teal-dim);
  border-color: var(--teal);
  color: var(--teal);
}
.logo-list {
  display: grid;
  overflow-y: auto;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  max-height: 14rem;
  gap: 5px;
  align-items: start;
}

.logo-item {
  width: 100%;
  aspect-ratio: 1 / 1;
  border: 1px solid var(--bg-picker-border);
  border-radius: 0.25rem;
  background: var(--bg-picker-surface);
  overflow: hidden;
}

.logo-item__thumb {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-item__thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
  display: block;
}

.logo-item__name {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: var(--bg-picker-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.logo-item__delete {
  width: 1rem;
  height: 1rem;
  border: none;
  border-radius: 9999px;
  background-color: #ef4444;
  color: #fff;
  cursor: pointer;
  line-height: 1;
  opacity: 0;
  position: absolute;
  top: 0.125rem;
  right: 0.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-item__delete i {
  font-size: 0.625rem;
}

.logo-item__delete:hover {
  opacity: 1;
}
.empty-folder {
  color: #64748b;
  font-size: 11px;
  font-style: italic;
  text-align: center;
  padding: 0.75rem 0.25rem;
}
.border-t {
  border-top: 1px solid #e2e8f0;
}
.file-browser-btn {
  color: #64748b !important;
  font-size: 10px;
  display: flex;
  align-items: center;
}
.file-browser-btn:hover {
  text-decoration: underline;
  color: var(--bg-picker-teal) !important;
}
</style>
